/**
 * @fileoverview Lightweight inline-SVG illustrations for the standard grid help panel.
 * @module ui-admin/help/gridHelpIllustrations
 *
 * Responsibilities: - Provide small, self-contained SVG figures shown atop each section of the
 *                     shared grid help panel (overview, filters/queries, columns/templates,
 *                     bulk operations, detail slide-in).
 * Dependencies:     - React (function components returning SVG).
 * Architecture:     - Pure presentation; static markup bundled with the app (local-first by
 *                     construction — available offline, no API/Dexie). No props, no state.
 * Notes:            - Keyed by GridHelpIllustrationId so help content references them by a
 *                     compile-time-checked id; the registry is consumed by HelpPanel.
 */

import React from 'react';

/** Stable identifier for a grid-help illustration. */
export type GridHelpIllustrationId =
  | 'gridOverview'
  | 'filtersQueries'
  | 'columnsTemplates'
  | 'bulkOps'
  | 'detailSlideIn';

const frameClass = 'w-full rounded-lg border border-gray-200 bg-gray-50';

/** Grid + toolbar overview. */
const GridOverview: React.FC = () => (
  <svg viewBox="0 0 360 150" className={frameClass} role="img" aria-label="Grid layout overview">
    <rect x="12" y="14" width="120" height="14" rx="4" fill="#fdba74" />
    <rect x="250" y="12" width="44" height="18" rx="5" fill="#e5e7eb" />
    <rect x="302" y="12" width="46" height="18" rx="5" fill="#e5e7eb" />
    <rect x="12" y="40" width="336" height="22" rx="5" fill="#fff" stroke="#e5e7eb" />
    <rect x="20" y="47" width="90" height="8" rx="4" fill="#d1d5db" />
    <rect x="12" y="70" width="336" height="68" rx="6" fill="#fff" stroke="#e5e7eb" />
    {[78, 96, 114].map((y) => (
      <g key={y}>
        <rect x="22" y={y} width="70" height="8" rx="4" fill="#e5e7eb" />
        <rect x="120" y={y} width="100" height="8" rx="4" fill="#e5e7eb" />
        <rect x="244" y={y} width="90" height="8" rx="4" fill="#e5e7eb" />
      </g>
    ))}
  </svg>
);

/** Filters / search / advanced query. */
const FiltersQueries: React.FC = () => (
  <svg viewBox="0 0 360 150" className={frameClass} role="img" aria-label="Search, filters and advanced query">
    <rect x="12" y="16" width="180" height="22" rx="6" fill="#fff" stroke="#e5e7eb" />
    <circle cx="26" cy="27" r="6" fill="none" stroke="#9ca3af" strokeWidth="2" />
    <rect x="40" y="23" width="120" height="8" rx="4" fill="#e5e7eb" />
    <rect x="204" y="16" width="64" height="22" rx="6" fill="#fff7ed" stroke="#fdba74" />
    <rect x="214" y="23" width="44" height="8" rx="4" fill="#fdba74" />
    <rect x="280" y="16" width="68" height="22" rx="6" fill="#fff7ed" stroke="#fdba74" />
    <rect x="290" y="23" width="48" height="8" rx="4" fill="#fdba74" />
    <rect x="12" y="56" width="336" height="80" rx="6" fill="#fff" stroke="#e5e7eb" />
    <rect x="24" y="68" width="60" height="16" rx="8" fill="#dbeafe" />
    <rect x="92" y="72" width="24" height="8" rx="4" fill="#93c5fd" />
    <rect x="24" y="96" width="60" height="16" rx="8" fill="#dcfce7" />
    <rect x="92" y="100" width="24" height="8" rx="4" fill="#86efac" />
    <rect x="130" y="70" width="200" height="10" rx="5" fill="#f3f4f6" />
    <rect x="130" y="98" width="160" height="10" rx="5" fill="#f3f4f6" />
  </svg>
);

/** Column settings + saved grid templates. */
const ColumnsTemplates: React.FC = () => (
  <svg viewBox="0 0 360 150" className={frameClass} role="img" aria-label="Column settings and saved grid templates">
    <rect x="12" y="16" width="150" height="118" rx="6" fill="#fff" stroke="#e5e7eb" />
    {[28, 48, 68, 88, 108].map((y, i) => (
      <g key={y}>
        <rect x="22" y={y} width="14" height="14" rx="3" fill={i % 2 === 0 ? '#fdba74' : '#e5e7eb'} />
        <rect x="44" y={y + 3} width="100" height="8" rx="4" fill="#e5e7eb" />
      </g>
    ))}
    <rect x="186" y="16" width="162" height="118" rx="6" fill="#fff" stroke="#e5e7eb" />
    <rect x="196" y="26" width="90" height="8" rx="4" fill="#fdba74" />
    {[46, 70, 94].map((y) => (
      <rect key={y} x="196" y={y} width="142" height="18" rx="5" fill="#f9fafb" stroke="#e5e7eb" />
    ))}
    <rect x="206" y={51} width="80" height="8" rx="4" fill="#d1d5db" />
    <rect x="206" y={75} width="100" height="8" rx="4" fill="#d1d5db" />
    <rect x="206" y={99} width="70" height="8" rx="4" fill="#d1d5db" />
  </svg>
);

/** Double-click to multi-select and run bulk actions. */
const BulkOps: React.FC = () => (
  <svg viewBox="0 0 360 150" className={frameClass} role="img" aria-label="Multi-select rows and run bulk actions">
    <rect x="12" y="14" width="336" height="22" rx="5" fill="#fff7ed" stroke="#fdba74" />
    <rect x="22" y="21" width="120" height="8" rx="4" fill="#fdba74" />
    <rect x="250" y="18" width="42" height="14" rx="4" fill="#fb923c" />
    <rect x="298" y="18" width="42" height="14" rx="4" fill="#fb923c" />
    <rect x="12" y="44" width="336" height="92" rx="6" fill="#fff" stroke="#e5e7eb" />
    {[54, 76, 98, 120].map((y, i) => (
      <g key={y}>
        <rect x="22" y={y} width="14" height="14" rx="3" fill={i < 2 ? '#fb923c' : '#e5e7eb'} />
        {i < 2 && <path d={`M25 ${y + 7} l3 3 l5 -6`} stroke="#fff" strokeWidth="2" fill="none" />}
        <rect x="44" y={y + 3} width="120" height="8" rx="4" fill="#e5e7eb" />
        <rect x="190" y={y + 3} width="130" height="8" rx="4" fill="#e5e7eb" />
      </g>
    ))}
  </svg>
);

/** Detail slide-in opening from a selected row. */
const DetailSlideIn: React.FC = () => (
  <svg viewBox="0 0 360 150" className={frameClass} role="img" aria-label="Detail slide-in panel">
    <rect x="12" y="16" width="206" height="118" rx="6" fill="#fff" stroke="#e5e7eb" />
    {[28, 50, 72, 94].map((y, i) => (
      <rect key={y} x="22" y={y} width="186" height="16" rx="4"
        fill={i === 1 ? '#fff7ed' : '#f9fafb'} stroke={i === 1 ? '#fdba74' : '#e5e7eb'} />
    ))}
    <rect x="232" y="16" width="116" height="118" rx="6" fill="#fff" stroke="#fdba74" />
    <rect x="232" y="16" width="116" height="22" rx="6" fill="#fff7ed" />
    <rect x="242" y="24" width="60" height="8" rx="4" fill="#fdba74" />
    <rect x="242" y="48" width="22" height="8" rx="4" fill="#fb923c" />
    <rect x="270" y="48" width="22" height="8" rx="4" fill="#e5e7eb" />
    <rect x="298" y="48" width="22" height="8" rx="4" fill="#e5e7eb" />
    <rect x="242" y="68" width="96" height="8" rx="4" fill="#f3f4f6" />
    <rect x="242" y="84" width="96" height="8" rx="4" fill="#f3f4f6" />
    <rect x="242" y="100" width="70" height="8" rx="4" fill="#f3f4f6" />
  </svg>
);

/**
 * @reusable
 * Usage: pass as the `illustrations` prop to HelpPanel alongside GRID_HELP_SECTIONS.
 * Rules: presentation-only registry of static SVG components — no state, no side effects.
 */
export const GRID_HELP_ILLUSTRATIONS: Partial<Record<GridHelpIllustrationId, React.FC>> = {
  gridOverview: GridOverview,
  filtersQueries: FiltersQueries,
  columnsTemplates: ColumnsTemplates,
  bulkOps: BulkOps,
  detailSlideIn: DetailSlideIn,
};
